/**
 * Pure formatting helpers for the review primitives. No React, no DOM —
 * unit-tested at the Node-only root vitest. Extracted verbatim-in-behavior
 * from the inline helpers in WallOfLoveSection / ReviewsFeedSection.
 */

/** Round a rating to the nearest whole star, clamped to 0..5. */
export function clampStars(rating: number): number {
  return Math.max(0, Math.min(5, Math.round(rating)))
}

/** Up to `max` leading word-initials, uppercased. '?' when the name is blank. */
export function initialsFor(name: string, max = 2): string {
  const initials = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .slice(0, max)
    .join('')
    .toUpperCase()
  return initials || '?'
}

/** Google-avatar-style palette, picked deterministically per name so a
 *  reviewer's initials circle is stable across renders. */
const AVATAR_COLORS = [
  '#7c4dff', '#00897b', '#5c6bc0', '#e53935', '#8e24aa',
  '#00acc1', '#43a047', '#fb8c00', '#546e7a', '#d81b60',
]
export function colorForName(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

/** US medium date ("Jun 1, 2026") or '' for an unparseable ISO string.
 *
 *  Pins timeZone:'UTC' for the same reason `formatArticleDate` does: without
 *  it the runtime's ambient zone decides the calendar day, so a review stamped
 *  just after UTC midnight renders one day on the server (Vercel runs UTC) and
 *  the previous day in a visitor's browser west of Greenwich. These components
 *  are server-rendered and then hydrated, so that difference is a hydration
 *  text mismatch — React discards the server tree and re-renders the document,
 *  which duplicates every inline <script> it can't dedupe, including the
 *  layout's LocalBusiness JSON-LD. Two nodes on one @id, each carrying
 *  aggregateRating, is what Search Console reports as "Review has multiple
 *  aggregate ratings". A review date has no clock component on screen, so UTC
 *  is also the honest label. */
export function formatReviewDate(iso: string): string {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
}

/** "66 reviews" / "1 review". The single source of the count phrasing shared
 *  by the floating indicator, the aggregate teaser, and their aria-labels. */
export function reviewCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'review' : 'reviews'}`
}

/** "5/5", "4.7/5" — the aggregate stated as a fraction for the hub's rating
 *  figure. A whole average drops the ".0" ("5/5", not "5.0/5"): the point of
 *  the figure is that the rating is perfect, and the decimal only adds noise. */
export function formatAverageOutOfFive(average: number): string {
  const rounded = Math.round(average * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}/5`
}

/**
 * Recency phrasing for a review ("5 days ago"), for surfaces where HOW FRESH
 * the review is carries the meaning and the calendar date does not. A visitor
 * reading "Jul 26, 2026" has to work out whether that is recent; "5 days ago"
 * says it. Coarse by design — a review is not an event log, so this stops at
 * whole days/months/years rather than pretending to minute precision on a
 * `review_date` that is frequently date-only.
 *
 * `now` is injectable so the output is deterministic under test. Returns ''
 * for an unparseable date (same contract as `formatReviewDate`) so the caller
 * can drop the line entirely. A date slightly in the FUTURE — timezone skew on
 * a date-only value, a platform clock ahead of ours — reads "just now" rather
 * than a negative age.
 */
export function formatRelativeReviewDate(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const seconds = Math.round((now - then) / 1000)
  if (seconds < 45) return 'just now'
  if (seconds < 90) return 'a minute ago'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minutes ago`
  const hours = Math.round(seconds / 3600)
  if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`
  const days = Math.round(seconds / 86400)
  if (days < 30) return days === 1 ? 'yesterday' : `${days} days ago`
  const months = Math.round(days / 30)
  if (months < 12) return months === 1 ? 'a month ago' : `${months} months ago`
  const years = Math.round(days / 365)
  return years === 1 ? 'a year ago' : `${years} years ago`
}

/** Preview length for a collapsed review body, in characters. Sized so a
 *  typical review fills roughly four lines in the ~20rem hub panel — long
 *  enough to carry a whole thought, short enough that two reviews are visible
 *  at once without scrolling. */
const REVIEW_PREVIEW_CHARS = 190

/**
 * Split a review body into a preview and a "was there more?" flag, so a
 * surface can offer a Read more affordance instead of silently swallowing the
 * rest. Cuts on a word boundary when one falls in the last 40% of the budget
 * (otherwise a single long word would strand the preview far short of it) and
 * appends an ellipsis, so the visitor can SEE the text was cut.
 *
 * Character-based rather than CSS line-clamped on purpose: `truncated` has to
 * be known at render time to decide whether the control exists at all, and a
 * clamp only reveals that after layout — which on the server, where this panel
 * first renders, is never. Pure and testable as a consequence.
 */
export function truncateReviewText(
  text: string,
  max: number = REVIEW_PREVIEW_CHARS,
): { preview: string; truncated: boolean } {
  const trimmed = text.trim()
  if (trimmed.length <= max) return { preview: trimmed, truncated: false }
  const cut = trimmed.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  const head = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut
  return { preview: `${head.trimEnd()}…`, truncated: true }
}
